import { DT } from "../../constants.ts";
import { add, norm, scale, sub, type Vec3 } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { addDamageFilter, aim, applyDamage, applyImpulse, damageBarrier, explode, lockOn, removeDamageFilters, type DamageInfo } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { FxKind } from "../../sim/events.ts";
import { beginTargeting, endTargeting, targetConfirmed, targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, glide, launchProjectile, ownerAlive, ready, secs, simpleProjectile, spreadDir, trigger, useUlt } from "../kitlib.ts";

export const V = { curtainUp: 0, curtainHp: 1, curtainRegenDelay: 2, rings: 3, meteorChannel: 4 } as const;
export const PK = { Orb: pk(HeroId.Magnetron, 0), Cannon: pk(HeroId.Magnetron, 1), Meteor: pk(HeroId.Magnetron, 2) } as const;
export const CK = { Curtain: ck(HeroId.Magnetron, 0) } as const;
export const MAGNETRON_BARRIER = { upVar: V.curtainUp, hpVar: V.curtainHp, maxHp: 100, width: 5.4, height: 3.2, forward: 1.7, regen: 11, regenDelay: 60 } as const;
const METEOR_RANGE = 70;
const METEOR_MAX_SECONDS = 5;
const METEOR_PREVIEW_RADIUS = 8;
const METEOR_SPEED = 45;
const METEOR_GRAVITY = 4;

const def: HeroDef = {
  id: HeroId.Magnetron,
  slug: "magnetron",
  name: "Magnetron",
  role: Role.Tank,
  color: "#b8324a",
  blurb: "Regal magnetic vanguard who blocks fire, shields allies and converts absorbed pressure into heavy metal shots.",
  health: 600,
  armor: 0,
  shields: 0,
  radius: 0.58,
  height: 2.15,
  headRadius: 0.28,
  speed: 6.0,
  jumpSpeed: 7.45,
  ammo: 10,
  reloadTime: 1.8,
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Lodestone Volley", key: "LMB", description: "Fire slow magnetic orbs that burst at range.", cooldown: 0 },
    [Slot.Secondary]: { name: "Ring Cannon", key: "RMB", description: "Spend stored rings to fire a stronger metal slug.", cooldown: 0 },
    [Slot.Ability1]: { name: "Magnet Curtain", key: "Shift (hold)", description: "Hold a frontal magnetic barrier with a 100-point resource.", cooldown: 0 },
    [Slot.Ability2]: { name: "Alloy Aegis", key: "E", description: "Shield the ally under your crosshair, or yourself if none is found.", cooldown: 15 },
    [Slot.Ability3]: { name: "Lodestone Rampart", key: "F", description: "Shield yourself with a faster-recharging magnetic guard.", cooldown: 10 },
    [Slot.Ultimate]: { name: "Scrapheap Meteor", key: "Q", description: "Channel up to 5 seconds with an impact reticle; press LMB to release a metal meteor charged by rings.", cooldown: 0 },
  },
  passive: { name: "Magnetic Descent", description: "Hold Space while airborne to fall at 3.5 m/s." },
};

function gainRing(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, amount: number) {
  setHv(sim.c, eid, V.rings, Math.min(3, hv(sim.c, eid, V.rings) + amount));
}

function applyBulwark(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, target: number, amount: number) {
  if (!sim.authority) return;
  let remaining = amount;
  removeDamageFilters(sim, target, owner, "magnetron-bulwark");
  addDamageFilter(sim, target, owner, {
    seconds: 2.5,
    tag: "magnetron-bulwark",
    filter(s, _target, incoming) {
      const absorbed = Math.min(remaining, incoming);
      remaining -= absorbed;
      gainRing(s, owner, absorbed / 100);
      return incoming - absorbed;
    },
  });
}

function meteorDir(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, target: Vec3) {
  const origin = aim(sim, eid).origin;
  let point = add(target, { x: 0, y: 0.7, z: 0 });
  let dir = norm(sub(point, origin));
  for (let i = 0; i < 2; i++) {
    const t = Math.min(2.2, Math.hypot(point.x - origin.x, point.y - origin.y, point.z - origin.z) / METEOR_SPEED);
    point = add(target, { x: 0, y: 0.7 + 0.5 * METEOR_GRAVITY * t * t, z: 0 });
    dir = norm(sub(point, origin));
  }
  return dir;
}

function abortMeteor(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  setHv(sim.c, eid, V.meteorChannel, 0);
  sim.c.Ult.active[eid] = 0;
  endTargeting(sim, eid);
}

function releaseMeteor(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, input: Parameters<HeroModule["kit"]["tick"]>[2]) {
  const c = sim.c;
  const rings = hv(c, eid, V.rings);
  const t = targetPoint(sim, eid, Slot.Ultimate).pos;
  setHv(c, eid, V.rings, 0);
  setHv(c, eid, V.meteorChannel, 0);
  c.Ult.active[eid] = 0;
  endTargeting(sim, eid);
  action(sim, eid, ActionKind.Ult, 34);
  launchProjectile(sim, eid, PK.Meteor, METEOR_SPEED, input, { radius: 0.7, seconds: 2.2, gravity: METEOR_GRAVITY, dir: meteorDir(sim, eid, t), target: Math.round(rings) });
}

const module: HeroModule = {
  def,
  barrier: MAGNETRON_BARRIER,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.curtainHp, MAGNETRON_BARRIER.maxHp);
      setHv(sim.c, eid, V.rings, 3);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      const delay = hv(c, eid, V.curtainRegenDelay);
      if (delay > 0) setHv(c, eid, V.curtainRegenDelay, delay - 1);
      else if (!hv(c, eid, V.curtainUp)) setHv(c, eid, V.curtainHp, Math.min(MAGNETRON_BARRIER.maxHp, hv(c, eid, V.curtainHp) + MAGNETRON_BARRIER.regen * DT));
      commonInput(sim, eid, input, pressed);
      const curtain = canAct && !!(input.buttons & Btn.Ability1) && hv(c, eid, V.curtainHp) > 0 && hv(c, eid, V.curtainRegenDelay) < 240;
      setHv(c, eid, V.curtainUp, curtain ? 1 : 0);
      if (curtain && sim.authority && sim.tick % 20 === 0) gainRing(sim, eid, 0.15);
      if (hv(c, eid, V.meteorChannel) > 0) {
        if (!canAct) abortMeteor(sim, eid);
        else {
          c.Vel.y[eid] = Math.max(c.Vel.y[eid]!, -0.8);
          if (targetConfirmed(sim, eid, Slot.Ultimate) || c.Ult.active[eid]! <= 1) releaseMeteor(sim, eid, input);
        }
        return;
      }
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        setHv(c, eid, V.meteorChannel, 1);
        c.Ult.active[eid] = secs(METEOR_MAX_SECONDS) + 1;
        beginTargeting(sim, eid, Slot.Ultimate);
        action(sim, eid, ActionKind.Ult, 34);
        return;
      }

      if ((pressed & Btn.Ability3) && ready(sim, eid, Slot.Ability3)) {
        consume(sim, eid, Slot.Ability3);
        action(sim, eid, ActionKind.Cast, 16);
        applyBulwark(sim, eid, eid, 200);
        gainRing(sim, eid, 0);
      }

      if ((pressed & Btn.Ability2) && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 16);
        const ally = lockOn(sim, eid, 20, 0.35, true) ?? eid;
        applyBulwark(sim, eid, ally, 200);
        gainRing(sim, eid, 0);
      }

      if ((input.buttons & Btn.Secondary) && c.Weapon.fireCd[eid]! <= 0) {
        const rings = Math.floor(hv(c, eid, V.rings));
        if (rings > 0) {
          setHv(c, eid, V.rings, 0);
          c.Weapon.fireCd[eid] = 52;
          action(sim, eid, ActionKind.AltFire, 16);
          launchProjectile(sim, eid, PK.Cannon, 120, input, { radius: 0.3, seconds: 1.2, target: rings });
        }
      } else if (!curtain && trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = 48;
        action(sim, eid, ActionKind.Fire, 12);
        launchProjectile(sim, eid, PK.Orb, 80, input, { radius: 0.22, seconds: 0.3125, dir: spreadDir(aim(sim, eid).dir, 0.01, c.Weapon.shots[eid]!) });
      }
    },
    movement(sim, eid, input, mods) {
      if (hv(sim.c, eid, V.curtainUp)) mods.speedMul *= 0.65;
      if (hv(sim.c, eid, V.meteorChannel)) {
        mods.speedMul *= 0.45;
        mods.gravityMul *= 0.15;
      }
      if (input.buttons & Btn.Jump) glide(sim, eid, input, mods, { fallSpeed: 3.5, driftSpeed: 1.2 });
    },
    onDealtDamage(sim, eid, _target, amount, _info: DamageInfo) {
      gainRing(sim, eid, amount / 260);
    },
    onDeath(sim, eid) {
      if (!sim.authority) return;
      for (const p of sim.playerEids()) removeDamageFilters(sim, p, eid, "magnetron-bulwark");
    },
  },
  projectiles: {
    [PK.Orb]: simpleProjectile(
      (sim, p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        if (hit.target !== undefined) applyDamage(sim, hit.target, 40, owner, { pos: hit.pos });
        else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, 40, owner);
        explode(sim, owner, hit.pos, { radius: 3, damage: 40, minDamage: 20, fxKind: FxKind.Grenade, skip: p });
      },
      { color: "#d6d8ff", size: 0.23, trail: true },
    ),
    [PK.Cannon]: simpleProjectile(
      (sim, _p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
        const rings = Math.max(1, Math.min(3, Math.round(sim.c.Projectile.target[_p] ?? 1)));
        const damage = rings === 1 ? 40 : rings === 2 ? 65 : 90;
        if (hit.target !== undefined) {
          applyDamage(sim, hit.target, damage, owner, { pos: hit.pos });
          if (rings >= 3) applyImpulse(sim, hit.target, scale(aim(sim, owner, 1, false).dir, 6));
        } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, damage, owner);
        else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, damage, owner);
        sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Grenade });
      },
      { color: "#ff8aa0", size: 0.3, trail: true },
    ),
    [PK.Meteor]: simpleProjectile(
      (sim, p, owner, hit) => {
        if (!ownerAlive(sim, owner)) return;
          const rings = Math.max(0, Math.min(3, sim.c.Projectile.target[p]!));
          const damage = 100 + rings * (250 / 3);
          explode(sim, owner, hit.pos, { radius: 5 + rings, damage, minDamage: damage * 0.5, knockback: 8, fxKind: FxKind.Singularity, skip: p });
        },
      { color: "#ff4e67", size: 0.7, trail: true },
    ),
  },
  constructs: {
    [CK.Curtain]: {
      maxHp: 1,
      seconds: 0.1,
      shape: { kind: "box", hw: 2, hd: 0.2, h: 2 },
      solid: false,
      blocksShots: true,
      damageable: false,
      visual: { color: "#d6d8ff" },
    },
  },
  targets: {
    [Slot.Ultimate]: { range: METEOR_RANGE, radius: METEOR_PREVIEW_RADIUS, start: "kit", timeout: METEOR_MAX_SECONDS, verb: "Release" },
  },
};

export default module;
